import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { useConfirm } from 'material-ui-confirm';

// Use unified date utils
import { moment } from '../../../utils/dateUtils';
import { isItemExpired } from '../../../utils/proposalCalculations';

import {
  Box,
  Button,
  Typography,
  Grid,
  TextField,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  useMediaQuery,
  useTheme,
  Stack,
  Chip,
} from '@mui/material';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';

// Icons
import AddIcon from '@mui/icons-material/Add';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';

// Utils
import messages from '../../../static/messages';

// Services
import proposalsServices from '../../../services/proposalsServices';
import discountsServices from '../../../services/discountsServices';

// Drag and Drop
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';
import { arrayMoveImmutable } from 'array-move';

// Auto-save
import useAutoSave from '../../../hooks/useAutoSave';
import AutoSaveIndicator from '../../AutoSaveIndicator/AutoSaveIndicator';

// Modal
import DiscountsSelectModal from '../../Modals/DiscountsSelectModal';

// Simple ID generator
const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// DnD Components
const DragHandle = SortableHandle(() => (
  <DragIndicatorIcon sx={{ cursor: 'grab', color: '#999', '&:hover': { color: '#333' }, mr: 1 }} />
));

const SortableItem = SortableElement(({ children }) => <div>{children}</div>);
const SortContainer = SortableContainer(({ children }) => <div>{children}</div>);

const ProposalDiscountsSection = ({ proposal, id, refresh }) => {
  const [discounts, setDiscounts] = useState([]);
  const [discountsCatalog, setDiscountsCatalog] = useState([]);
  const [selectModalOpen, setSelectModalOpen] = useState(false);
  const isSigned = !!(proposal?.signatureId || proposal?.signature);
  const dirtyRef = useRef(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const confirm = useConfirm();

  // Load discounts catalog
  useEffect(() => {
    const loadCatalog = async () => {
      try {
        const response = await discountsServices.getAll({ size: 1000 });
        const data = response.data?.data || response.data || [];
        setDiscountsCatalog(Array.isArray(data) ? data : []);
      } catch (error) {
        console.log('Failed to load discounts catalog:', error);
      }
    };
    loadCatalog();
  }, []);

  // Auto-save function
  const saveDiscounts = useCallback(async (data) => {
    const response = await proposalsServices.update(id, { discounts: data });
    dirtyRef.current = false;
    if (response.status === 200 && refresh) {
      refresh();
    }
  }, [id, refresh]);

  // Auto-save hook — no timer. Text saves on word boundaries (space/period)
  // and blur, numbers on blur, discrete actions (checkbox/date/add/delete)
  // save immediately.
  const {
    isSaving,
    hasUnsavedChanges,
    lastSaved,
    error,
    markPending,
    saveNow,
    getLastSavedText,
  } = useAutoSave(saveDiscounts);

  const saveNowSafe = useCallback((data) => {
    saveNow(data).catch(() => {
      toast.error('Auto-save failed. Your changes will be retried on the next edit.');
    });
  }, [saveNow]);

  // Flush pending edits (last word may have no trailing space/period)
  const flushPending = useCallback(() => {
    saveNowSafe();
  }, [saveNowSafe]);

  // Add single custom discount
  const handleAddDiscount = () => {
    const newDiscount = {
      id: generateId(),
      name: '',
      amount: 0,
      expires: moment().endOf('month').format('YYYY-MM-DD'),
      isSelected: true,
    };
    const updated = [...discounts, newDiscount];
    setDiscounts(updated);
    // Don't trigger save for empty discount - wait for user to fill it
  };

  // Add multiple discounts from catalog
  const handleAddFromCatalog = (selectedItems) => {
    const newDiscounts = selectedItems.map(item => ({
      id: generateId(),
      name: item.description,
      amount: item.amount || 0,
      expires: moment().add(item.defaultExpirationDays || 30, 'days').format('YYYY-MM-DD'),
      isSelected: true,
    }));
    const updated = [...discounts, ...newDiscounts];
    setDiscounts(updated);
    saveNowSafe(updated);
  };

  const handleChange = (discountId, field, value) => {
    const updated = discounts.map((d) =>
      d.id === discountId
        ? { ...d, [field]: field === 'amount' ? Number(value) || 0 : value }
        : d
    );
    setDiscounts(updated);
    dirtyRef.current = true;

    if (field === 'name' || field === 'amount') {
      // Typed input: no timer. The change stays pending and is committed by
      // the word-boundary keyup (name), blur, or the next discrete action.
      markPending(updated);
      return;
    }

    // Discrete changes (checkbox, date picker) save immediately
    saveNowSafe(updated);
  };

  const handleDelete = (discountId) => {
    confirm({
      title: messages.confirm_message.remove,
      description: messages.confirm_message.remove_description('this discount'),
    })
      .then(() => {
        const updated = discounts.filter((d) => d.id !== discountId);
        setDiscounts(updated);
        saveNowSafe(updated);
      })
      .catch(() => toast.info(messages.cancel_message));
  };

  // Handle drag and drop reorder
  const handleSortEnd = ({ oldIndex, newIndex }) => {
    if (oldIndex === newIndex) return;
    const updated = arrayMoveImmutable(discounts, oldIndex, newIndex);
    setDiscounts(updated);
    saveNowSafe(updated);
  };

  const totalDiscounts = discounts
    .filter((d) => d.isSelected)
    .reduce((sum, d) => sum + (Number(d.amount) || 0), 0);

  // Hooks
  useEffect(() => {
    if (proposal?.discounts && !dirtyRef.current) {
      setDiscounts(proposal.discounts);
    }
  }, [proposal]);

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'flex-start' : 'center',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 1 : 0,
        mb: 2 
      }}>
        <Typography variant={isMobile ? 'subtitle1' : 'h6'}>
          Discounts (Instants)
          <Typography component="span" sx={{ ml: 2, color: 'success.main', fontWeight: 600, fontSize: isMobile ? '0.875rem' : 'inherit' }}>
            Total: ${totalDiscounts.toFixed(2)}
          </Typography>
        </Typography>
        <AutoSaveIndicator
            isSaving={isSaving}
            hasUnsavedChanges={hasUnsavedChanges}
            lastSaved={lastSaved}
            error={error}
            getLastSavedText={getLastSavedText}
            variant="chip"
          />
      </Box>

      {discounts.length === 0 ? (
        <Typography color="text.secondary" sx={{ fontStyle: 'italic', mb: 2 }}>
          No discounts added. Use buttons below to add discounts.
        </Typography>
      ) : (
        <SortContainer useDragHandle onSortEnd={handleSortEnd}>
          {discounts.map((discount, index) => (
            <SortableItem key={discount.id} index={index}>
              <Box sx={{ 
                mb: 2, 
                p: isMobile ? 1.5 : 2, 
                border: isItemExpired(discount, isSigned) ? '1px solid #ef5350' : '1px solid #e0e0e0', 
                borderRadius: 1,
                backgroundColor: isItemExpired(discount, isSigned) ? '#fff5f5' : 'transparent',
                opacity: isItemExpired(discount, isSigned) ? 0.75 : 1,
              }}>
                <Grid container spacing={isMobile ? 1.5 : 2} alignItems="center">
                  {/* Drag handle + Active checkbox row on mobile */}
                  <Grid item xs={isMobile ? 12 : 'auto'} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <DragHandle />
                      {isItemExpired(discount, isSigned) && (
                        <Chip label="EXPIRED" size="small" color="error" sx={{ height: 20, fontSize: 10, fontWeight: 700 }} />
                      )}
                      {isMobile && (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={discount.isSelected}
                              onChange={(e) => handleChange(discount.id, 'isSelected', e.target.checked)}
                              size="small"
                            />
                          }
                          label="Active"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Box>
                    {isMobile && (
                      <Tooltip title="Delete Discount">
                        <IconButton onClick={() => handleDelete(discount.id)} color="error" size="small">
                          <DeleteForeverIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Grid>
                  {/* Desktop only: Active checkbox */}
                  {!isMobile && (
                    <Grid item md={1}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={discount.isSelected}
                            onChange={(e) => handleChange(discount.id, 'isSelected', e.target.checked)}
                          />
                        }
                        label="Active"
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Discount Name"
                      value={discount.name}
                      onChange={(e) => handleChange(discount.id, 'name', e.target.value)}
                      onKeyUp={(e) => { if (e.key === ' ' || e.key === '.') flushPending(); }}
                      onBlur={flushPending}
                      placeholder="e.g., Yelp discount, Holiday special"
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Amount</InputLabel>
                      <OutlinedInput
                        type="number"
                        value={discount.amount || ''}
                        onChange={(e) => handleChange(discount.id, 'amount', e.target.value)}
                        onBlur={flushPending}
                        onKeyUp={(e) => { if (e.key === 'Enter') flushPending(); }}
                        startAdornment={<InputAdornment position="start">$</InputAdornment>}
                        label="Amount"
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <MobileDatePicker
                        label="Expires"
                        inputFormat="MM/DD/YYYY"
                        value={discount.expires}
                        onChange={(date) =>
                          handleChange(discount.id, 'expires', date ? date.format('YYYY-MM-DD') : null)
                        }
                        renderInput={(params) => <TextField fullWidth size="small" {...params} />}
                      />
                    </LocalizationProvider>
                  </Grid>
                  {/* Desktop only: Delete button */}
                  {!isMobile && (
                    <Grid item md={1}>
                      <Tooltip title="Delete Discount">
                        <IconButton onClick={() => handleDelete(discount.id)} color="error">
                          <DeleteForeverIcon />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </SortableItem>
          ))}
        </SortContainer>
      )}

      {/* Add buttons at bottom */}
      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<PlaylistAddIcon />}
          onClick={() => setSelectModalOpen(true)}
          color="success"
        >
          Add Multiple
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddDiscount}
        >
          Add Single
        </Button>
      </Stack>

      {/* Select from catalog modal */}
      <DiscountsSelectModal
        open={selectModalOpen}
        onClose={() => setSelectModalOpen(false)}
        discountsList={discountsCatalog}
        existingItems={discounts}
        onAdd={handleAddFromCatalog}
      />
    </Box>
  );
};

export default ProposalDiscountsSection;
